/**
 * SignalCompactBar - Data-Dense Single-Row Signal Summary
 *
 * Displays trading signal in compact single-row format.
 * Shows signal, confidence, entry zone in minimal space.
 * Expandable for detailed reasons/warnings.
 */

import React, { useState, memo } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Get signal color
 */
const getSignalColor = (signal) => {
    if (!signal) return '#6b7280';
    const s = signal.toUpperCase();
    if (s === 'BUY' || s === 'STRONG_BUY') return '#22c55e';
    if (s === 'SELL' || s === 'STRONG_SELL') return '#ef4444';
    return '#fbbf24';
};

/**
 * Get confidence color
 */
const getConfidenceColor = (confidence) => {
    if (!confidence && confidence !== 0) return '#6b7280';
    if (confidence >= 0.8) return '#22c55e';
    if (confidence >= 0.6) return '#4ade80';
    if (confidence >= 0.4) return '#fbbf24';
    return '#ef4444';
};

/**
 * Confidence Mini Progress Bar
 */
const ConfidenceMiniBar = ({ confidence }) => {
    const percentage = (confidence || 0) * 100;
    const color = getConfidenceColor(confidence);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: '50px', height: '6px', backgroundColor: 'rgba(55, 65, 81, 0.5)', borderRadius: '3px', overflow: 'hidden' }}>
                <Box sx={{ width: `${percentage}%`, height: '100%', backgroundColor: color, borderRadius: '3px' }} />
            </Box>
            <Typography sx={{ fontSize: '10px', color, fontWeight: 700, fontFamily: 'monospace', minWidth: '28px' }}>
                {percentage.toFixed(0)}%
            </Typography>
        </Box>
    );
};

/**
 * SignalCompactBar Component
 */
const SignalCompactBar = memo(({ data, currentState }) => {
    const signal = data?.signal;
    const hasReasons = (data?.reasons?.length > 0) || (data?.warnings?.length > 0);

    // Auto-expand when signal is BUY or SELL with high confidence
    const shouldAutoExpand = () => {
        if (!signal) return false;
        const s = signal.toUpperCase();
        const confidence = data?.confidence || 0;
        // Auto-expand for actionable signals with confidence > 50%
        return (s === 'BUY' || s === 'SELL' || s === 'STRONG_BUY' || s === 'STRONG_SELL') && confidence >= 0.5 && hasReasons;
    };

    const [expanded, setExpanded] = useState(shouldAutoExpand());
    const confidence = data?.confidence;
    const reasons = data?.reasons || [];
    const warnings = data?.warnings || [];
    const entryZone = data?.entryZone || {};
    const session = data?.session || currentState?.session;
    const timestamp = data?.timestamp;

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '2px', overflow: 'hidden' }}>
            {/* Main Signal Row */}
            <Box
                onClick={() => hasReasons && setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 8px',
                    cursor: hasReasons ? 'pointer' : 'default',
                    backgroundColor: 'rgba(34, 197, 94, 0.05)',
                    borderBottom: expanded ? '1px solid rgba(55, 65, 81, 0.3)' : 'none',
                    '&:hover': hasReasons ? { backgroundColor: 'rgba(55, 65, 81, 0.15)' } : {}
                }}
            >
                {/* Signal Badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            backgroundColor: getSignalColor(signal),
                            color: '#fff',
                            padding: '2px 10px',
                            borderRadius: '3px',
                            minWidth: '55px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'monospace' }}>
                            {signal || 'N/A'}
                        </Typography>
                    </Box>

                    {/* Confidence */}
                    <ConfidenceMiniBar confidence={confidence} />

                    {/* Entry Zone */}
                    {entryZone.idealEntry && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                ENTRY:
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#fbbf24', fontWeight: 700, fontFamily: 'monospace' }}>
                                {entryZone.idealEntry?.toFixed(2)}
                            </Typography>
                            {entryZone.fibLevel && (
                                <Typography sx={{ fontSize: '9px', color: '#a855f7', fontFamily: 'monospace' }}>
                                    @{entryZone.fibLevel}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Right Side: Session + Expand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {session && (
                        <Typography sx={{ fontSize: '9px', color: '#fbbf24', fontFamily: 'monospace', fontWeight: 600 }}>
                            {session}
                        </Typography>
                    )}
                    {hasReasons && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                                {reasons.length + warnings.length}
                            </Typography>
                            <ExpandMoreIcon
                                sx={{
                                    fontSize: 14,
                                    color: '#6b7280',
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Expanded Details */}
            <Collapse in={expanded}>
                <Box sx={{ padding: '6px 8px', backgroundColor: 'rgba(55, 65, 81, 0.1)' }}>
                    {/* Reasons */}
                    {reasons.length > 0 && (
                        <Box sx={{ marginBottom: warnings.length > 0 ? 1 : 0 }}>
                            <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace', marginBottom: 0.5 }}>
                                REASONS:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {reasons.map((reason, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                            padding: '2px 6px',
                                            borderRadius: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '9px', color: '#22c55e' }}>✓</Typography>
                                        <Typography sx={{ fontSize: '9px', color: '#4ade80', fontFamily: 'monospace' }}>
                                            {reason}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Warnings */}
                    {warnings.length > 0 && (
                        <Box>
                            <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace', marginBottom: 0.5 }}>
                                WARNINGS:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {warnings.map((warning, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            backgroundColor: 'rgba(251, 191, 36, 0.15)',
                                            padding: '2px 6px',
                                            borderRadius: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '9px', color: '#fbbf24' }}>⚠</Typography>
                                        <Typography sx={{ fontSize: '9px', color: '#fbbf24', fontFamily: 'monospace' }}>
                                            {warning}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Timestamp */}
                    {timestamp && (
                        <Typography sx={{ fontSize: '8px', color: '#6b7280', fontFamily: 'monospace', marginTop: 0.5, textAlign: 'right' }}>
                            Updated: {new Date(timestamp).toLocaleTimeString()}
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
});

SignalCompactBar.displayName = 'SignalCompactBar';

export default SignalCompactBar;
